import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth } from "@/App";
import { 
  Clock, 
  Shield, 
  Users, 
  Brain, 
  Heart,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Check,
  AlertCircle
} from "lucide-react";

const WELLNESS_QUESTIONS = [
  {
    id: 1,
    question: "Over the past 2 weeks, how often have you felt nervous, anxious, or on edge?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 2,
    question: "How often have you had trouble relaxing?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 3,
    question: "How often have you felt so restless that it's hard to sit still?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 4,
    question: "How often have you been easily annoyed or irritable?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 5,
    question: "How often have you felt afraid, as if something awful might happen?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 6,
    question: "How often have you had difficulty falling or staying asleep?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  },
  {
    id: 7,
    question: "How often have you felt tired or had little energy?",
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "Several days" },
      { value: 2, label: "More than half the days" },
      { value: 3, label: "Nearly every day" }
    ]
  }
];

const STATS = [
  {
    icon: Users,
    stat: "1 in 3",
    title: "Adults",
    description: "Report feeling stressed daily",
    gradient: "from-[#f0d8d8] to-[#e8c8c8]"
  },
  {
    icon: Brain,
    stat: "77%",
    title: "Of People",
    description: "Experience stress that affects their physical health",
    gradient: "from-[#d8d0e8] to-[#c8c0d8]"
  },
  {
    icon: Heart,
    stat: "43%",
    title: "Report",
    description: "That stress has increased in the past year",
    gradient: "from-[#d0e0e8] to-[#c0d0d8]"
  }
];

function getResultCategory(score) {
  if (score <= 4) return {
    level: "Minimal",
    color: "text-[#5a8a60]",
    bgColor: "bg-[#d4e8d8]",
    message: "Your stress levels appear to be well-managed. Keep up the good work with your self-care practices.",
    recommendations: [
      "Continue your current wellness routines",
      "Practice daily gratitude",
      "Maintain regular sleep schedule"
    ]
  };
  if (score <= 9) return {
    level: "Mild",
    color: "text-[#7a8a50]",
    bgColor: "bg-[#e0e8d0]",
    message: "You're experiencing some stress, which is normal. Consider incorporating more relaxation techniques.",
    recommendations: [
      "Try our breathing exercises daily",
      "Take short breaks throughout the day",
      "Consider journaling your thoughts"
    ]
  };
  if (score <= 14) return {
    level: "Moderate",
    color: "text-[#a08050]",
    bgColor: "bg-[#f0e0d0]",
    message: "Your stress levels are elevated. It's important to prioritize self-care and stress management.",
    recommendations: [
      "Practice breathing exercises twice daily",
      "Limit caffeine and screen time",
      "Reach out to friends or family",
      "Consider speaking with a professional"
    ]
  };
  return {
    level: "High",
    color: "text-[#a06060]",
    bgColor: "bg-[#f0d8d8]",
    message: "Your responses suggest high stress levels. Please consider reaching out to a mental health professional.",
    recommendations: [
      "Speak with a healthcare provider",
      "Use our breathing exercises for immediate relief",
      "Practice grounding techniques",
      "Don't hesitate to ask for help"
    ]
  };
}

export default function WellnessCheckPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("intro"); // intro, verify, questions, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [ageVerified, setAgeVerified] = useState(null);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };

  const handleNext = () => {
    if (currentQuestion < WELLNESS_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setStep("results");
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const progress = ((currentQuestion + 1) / WELLNESS_QUESTIONS.length) * 100;
  const result = step === "results" ? getResultCategory(calculateScore()) : null;

  // Intro Screen
  if (step === "intro") {
    return (
      <div className="min-h-screen bg-background">
        {/* Header with back button */}
        <div className="p-4 bg-gradient-to-br from-[#d0dce8] via-[#c8d8e8] to-[#d8d0e8]">
          <button 
            onClick={() => navigate("/compass")}
            className="p-2 -ml-2"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
        </div>
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#d0dce8] via-[#c8d8e8] to-[#d8d0e8] min-h-[50vh] flex items-center px-4 py-12 -mt-14">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading text-foreground mb-4">
              How are you<br />feeling right now?
            </h1>
            <p className="text-foreground/70 text-base sm:text-lg mb-6 max-w-md">
              Take a quick wellness check to understand your current stress levels and get personalized recommendations.
            </p>
            <p className="text-foreground/50 text-sm mb-8">
              This wellness check is not a substitute for professional medical advice.
            </p>
            <Button 
              className="btn-primary text-base py-5 px-8"
              onClick={() => setStep("verify")}
              data-testid="start-wellness-check"
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="px-4 py-12 max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#d0e0e8] flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-[#5a7090]" />
                </div>
                <h3 className="text-foreground font-heading text-lg mb-2">Takes 2-3 minutes</h3>
                <p className="text-foreground/60 text-sm">
                  Answer a few simple questions about how you've been feeling lately.
                </p>
              </CardContent>
            </Card>
            
            <Card className="glass-card">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#d8d0e8] flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#7a6090]" />
                </div>
                <h3 className="text-foreground font-heading text-lg mb-2">Your privacy matters</h3>
                <p className="text-foreground/60 text-sm">
                  All responses are confidential and will not be shared with anyone.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Stats Section */}
          <h2 className="text-2xl font-heading text-foreground text-center mb-8">Did you know?</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {STATS.map((stat) => (
              <Card key={stat.title} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className={`h-28 bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}>
                    <stat.icon className="w-12 h-12 text-foreground/60" />
                  </div>
                  <div className="p-4 bg-[#f5f9f5] text-center">
                    <p className="text-2xl font-heading text-foreground">{stat.stat}</p>
                    <p className="text-foreground font-medium text-sm">{stat.title}</p>
                    <p className="text-foreground/50 text-xs mt-1">{stat.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {user && <BottomNavigation />}
      </div>
    );
  }

  // Age Verification
  if (step === "verify") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#d0dce8] via-[#c8d8e8] to-[#d8d0e8] flex flex-col">
        <div className="p-4">
          <button 
            onClick={() => setStep("intro")}
            className="w-10 h-10 rounded-full bg-white/40 flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
        </div>
        
        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl sm:text-3xl font-heading text-foreground mb-8">
              Are you 18 years or older?
            </h1>
            
            <div className="space-y-3 mb-8">
              <button
                onClick={() => setAgeVerified(true)}
                className={`w-full p-4 rounded-full text-left px-6 transition-all ${
                  ageVerified === true 
                    ? 'bg-white shadow-md' 
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                data-testid="age-yes"
              >
                <span className="text-foreground font-medium">Yes</span>
              </button>
              <button
                onClick={() => setAgeVerified(false)}
                className={`w-full p-4 rounded-full text-left px-6 transition-all ${
                  ageVerified === false 
                    ? 'bg-white shadow-md' 
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                data-testid="age-no"
              >
                <span className="text-foreground font-medium">No</span>
              </button>
            </div>

            <p className="text-foreground/50 text-sm mb-8">
              By continuing, you agree to our Terms and acknowledge that you have read our Privacy Policy.
            </p>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1 border-foreground/20 text-foreground"
                onClick={() => setStep("intro")}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 btn-primary"
                disabled={ageVerified !== true}
                onClick={() => setStep("questions")}
                data-testid="continue-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Questions
  if (step === "questions") {
    const question = WELLNESS_QUESTIONS[currentQuestion];
    const selectedAnswer = answers[question.id];

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#d0dce8] via-[#c8d8e8] to-[#d8d0e8] flex flex-col">
        <div className="p-4 flex items-center justify-between">
          <button 
            onClick={() => currentQuestion === 0 ? setStep("verify") : handlePrev()}
            className="w-10 h-10 rounded-full bg-white/40 flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <span className="text-foreground/60 text-sm">
            {currentQuestion + 1} of {WELLNESS_QUESTIONS.length}
          </span>
          <div className="w-10" />
        </div>

        <div className="px-4 mb-4">
          <Progress value={progress} className="h-2 bg-white/40" />
        </div>
        
        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <div className="max-w-md w-full">
            <h2 className="text-xl sm:text-2xl font-heading text-foreground mb-8 text-center">
              {question.question}
            </h2>
            
            <div className="space-y-3 mb-8">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(question.id, option.value)}
                  className={`w-full p-4 rounded-full text-left px-6 transition-all ${
                    selectedAnswer === option.value 
                      ? 'bg-[#7a9e7a] shadow-md' 
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <span className={`font-medium ${selectedAnswer === option.value ? 'text-white' : 'text-foreground'}`}>{option.label}</span>
                </button>
              ))}
            </div>

            <Button 
              className="w-full btn-primary"
              disabled={selectedAnswer === undefined}
              onClick={handleNext}
              data-testid="next-btn"
            >
              {currentQuestion === WELLNESS_QUESTIONS.length - 1 ? "See Results" : "Next"}
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (step === "results" && result) {
    const totalScore = calculateScore();
    
    return (
      <div className="min-h-screen bg-background pb-28">
        {/* Results Header */}
        <div className="bg-background px-4 py-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-[#a69373]/20 border-4 border-[#a69373]/30 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl font-heading text-[#a69373]">{totalScore}</span>
            </div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Your Score</p>
            <h1 className="text-2xl sm:text-3xl font-heading text-[#a69373] mb-4">
              {result.level} Stress
            </h1>
            <p className="text-white/70 text-base leading-relaxed">{result.message}</p>
          </div>
        </div>

        <div className="px-4 py-8 max-w-md mx-auto">
          <h2 className="text-lg font-heading text-[#a69373] mb-4">Recommendations</h2>
          <div className="space-y-3 mb-8">
            {result.recommendations.map((rec, index) => (
              <Card key={`rec-${index}-${rec.slice(0, 20)}`} className="glass-card">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#a69373]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#a69373]" />
                  </div>
                  <p className="text-white/80 text-sm">{rec}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-3">
            <Button 
              className="w-full btn-primary"
              onClick={() => navigate("/breathe")}
            >
              Try Breathing Exercise
            </Button>
            <Button 
              variant="outline"
              className="w-full border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
              onClick={() => {
                setStep("intro");
                setAnswers({});
                setCurrentQuestion(0);
              }}
              data-testid="try-again-btn"
            >
              Try Again
            </Button>
            {user && (
              <Button 
                variant="ghost"
                className="w-full text-white/60"
                onClick={() => navigate("/compass")}
              >
                Back to Compass
              </Button>
            )}
          </div>

          <Card className="glass-card mt-8">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-white/40 shrink-0 mt-0.5" />
              <p className="text-white/50 text-xs">
                This wellness check is for informational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment.
              </p>
            </CardContent>
          </Card>
        </div>

        {user && <BottomNavigation />}
      </div>
    );
  }

  return null;
}
